/**
 * formulaEngine — dependency-free Excel-style formula evaluator.
 *
 * Scope (pragmatic subset covering everyday spreadsheet use):
 *   - literals: numbers, "strings", TRUE/FALSE
 *   - references: A1, $A$1, and ranges A1:B5, optionally sheet-qualified
 *                 (Sheet1!A1, 'My Sheet'!A1:B5)
 *   - operators: + - * / ^, unary minus, & (concat), = <> < <= > >=,
 *                postfix % (percent)
 *   - functions: SUM AVERAGE/AVG MIN MAX COUNT COUNTA COUNTBLANK PRODUCT
 *                IF AND OR NOT IFERROR
 *                COUNTIF SUMIF AVERAGEIF VLOOKUP HLOOKUP
 *                ROUND ROUNDUP ROUNDDOWN CEILING FLOOR ABS SQRT POWER MOD INT
 *                CONCATENATE CONCAT LEN LEFT RIGHT MID UPPER LOWER TRIM
 *                TODAY NOW
 *
 * evaluateFormula("=SUM(A1:A3)*2", getCell) -> number | string | boolean
 * where getCell(rowIndex0, colIndex0, sheetName?) returns the cell's raw
 * value; sheetName is only passed for sheet-qualified references.
 * On any error returns the string "#ERROR!" (Excel-like), never throws.
 */

/* ------------------------------------------------------------------ */
/* reference helpers                                                    */
/* ------------------------------------------------------------------ */

// "A" -> 0, "B" -> 1, "AA" -> 26 ...
function columnLabelToIndex(label) {
  let n = 0;
  for (let i = 0; i < label.length; i++) {
    n = n * 26 + (label.charCodeAt(i) - 64);
  }
  return n - 1;
}

// "A1" / "$A$1" -> { row: 0-based, col: 0-based }
function parseRef(ref) {
  const m = /^\$?([A-Za-z]+)\$?(\d+)$/.exec(ref);
  if (!m) return null;
  const col = columnLabelToIndex(m[1].toUpperCase());
  const row = parseInt(m[2], 10) - 1;
  if (row < 0 || col < 0) return null;
  return { row, col };
}

/* ------------------------------------------------------------------ */
/* tokenizer                                                            */
/* ------------------------------------------------------------------ */

function tokenize(src) {
  const tokens = [];
  let i = 0;
  const n = src.length;
  const isDigit = (c) => c >= "0" && c <= "9";
  const isAlpha = (c) => (c >= "A" && c <= "Z") || (c >= "a" && c <= "z");

  while (i < n) {
    const c = src[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") { i++; continue; }

    if (c === '"') {
      let s = "";
      i++;
      while (i < n) {
        if (src[i] === '"') {
          if (src[i + 1] === '"') { s += '"'; i += 2; continue; }
          i++; break;
        }
        s += src[i++];
      }
      tokens.push({ t: "str", v: s });
      continue;
    }

    if (isDigit(c) || (c === "." && isDigit(src[i + 1]))) {
      let num = "";
      while (i < n && (isDigit(src[i]) || src[i] === ".")) num += src[i++];
      tokens.push({ t: "num", v: parseFloat(num) });
      continue;
    }

    // Quoted sheet prefix: 'My Sheet'!A1
    if (c === "'") {
      let name = "";
      i++;
      while (i < n) {
        if (src[i] === "'") {
          if (src[i + 1] === "'") { name += "'"; i += 2; continue; }
          i++; break;
        }
        name += src[i++];
      }
      if (src[i] !== "!") throw new Error("Unexpected char: '");
      i++;
      let ref = "";
      while (i < n && (isAlpha(src[i]) || isDigit(src[i]) || src[i] === "$")) ref += src[i++];
      tokens.push({ t: "ref", v: ref, sheet: name });
      continue;
    }

    if (isAlpha(c) || c === "$" || c === "_") {
      let word = "";
      while (i < n && (isAlpha(src[i]) || isDigit(src[i]) || src[i] === "$" || src[i] === "_" || src[i] === ".")) {
        word += src[i++];
      }
      // Unquoted sheet prefix: Sheet1!A1
      if (src[i] === "!") {
        i++;
        let ref = "";
        while (i < n && (isAlpha(src[i]) || isDigit(src[i]) || src[i] === "$")) ref += src[i++];
        tokens.push({ t: "ref", v: ref, sheet: word });
        continue;
      }
      // function call?  (next non-space char is "(")
      let j = i;
      while (j < n && (src[j] === " " || src[j] === "\t")) j++;
      if (src[j] === "(") tokens.push({ t: "func", v: word.toUpperCase() });
      else if (/^(TRUE|FALSE)$/i.test(word)) tokens.push({ t: "bool", v: /^true$/i.test(word) });
      else tokens.push({ t: "ref", v: word });
      continue;
    }

    // two-char operators
    const two = src.substr(i, 2);
    if (two === "<=" || two === ">=" || two === "<>") { tokens.push({ t: "op", v: two }); i += 2; continue; }

    if ("+-*/^&=<>(),:%".indexOf(c) !== -1) { tokens.push({ t: "op", v: c }); i++; continue; }

    throw new Error("Unexpected char: " + c);
  }
  return tokens;
}

/* ------------------------------------------------------------------ */
/* parser (recursive descent) -> AST                                   */
/* ------------------------------------------------------------------ */

function parse(tokens) {
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const expect = (v) => { const t = next(); if (!t || t.v !== v) throw new Error("Expected " + v); };

  // precedence: comparison < concat < add < mul < power < unary
  function parseExpr() { return parseComparison(); }

  function parseComparison() {
    let left = parseConcat();
    while (peek() && peek().t === "op" && ["=", "<>", "<", "<=", ">", ">="].includes(peek().v)) {
      const op = next().v;
      left = { type: "bin", op, left, right: parseConcat() };
    }
    return left;
  }
  function parseConcat() {
    let left = parseAdd();
    while (peek() && peek().t === "op" && peek().v === "&") {
      next();
      left = { type: "bin", op: "&", left, right: parseAdd() };
    }
    return left;
  }
  function parseAdd() {
    let left = parseMul();
    while (peek() && peek().t === "op" && (peek().v === "+" || peek().v === "-")) {
      const op = next().v;
      left = { type: "bin", op, left, right: parseMul() };
    }
    return left;
  }
  function parseMul() {
    let left = parsePower();
    while (peek() && peek().t === "op" && (peek().v === "*" || peek().v === "/")) {
      const op = next().v;
      left = { type: "bin", op, left, right: parsePower() };
    }
    return left;
  }
  function parsePower() {
    let left = parseUnary();
    while (peek() && peek().t === "op" && peek().v === "^") {
      next();
      left = { type: "bin", op: "^", left, right: parseUnary() };
    }
    return left;
  }
  function parseUnary() {
    if (peek() && peek().t === "op" && peek().v === "-") { next(); return { type: "neg", arg: parseUnary() }; }
    if (peek() && peek().t === "op" && peek().v === "+") { next(); return parseUnary(); }
    return parsePostfix();
  }
  // Postfix percent: 10% -> 0.1, A1% -> A1/100.
  function parsePostfix() {
    let node = parsePrimary();
    while (peek() && peek().t === "op" && peek().v === "%") {
      next();
      node = { type: "pct", arg: node };
    }
    return node;
  }
  function parsePrimary() {
    const t = peek();
    if (!t) throw new Error("Unexpected end");
    if (t.t === "num") { next(); return { type: "num", v: t.v }; }
    if (t.t === "str") { next(); return { type: "str", v: t.v }; }
    if (t.t === "bool") { next(); return { type: "bool", v: t.v }; }
    if (t.t === "func") {
      const name = next().v;
      expect("(");
      const args = [];
      if (!(peek() && peek().v === ")")) {
        args.push(parseExpr());
        while (peek() && peek().v === ",") { next(); args.push(parseExpr()); }
      }
      expect(")");
      return { type: "call", name, args };
    }
    if (t.t === "ref") {
      next();
      // range?
      if (peek() && peek().t === "op" && peek().v === ":") {
        next();
        const end = next();
        return { type: "range", from: t.v, to: end.v, sheet: t.sheet };
      }
      return { type: "ref", ref: t.v, sheet: t.sheet };
    }
    if (t.t === "op" && t.v === "(") {
      next();
      const e = parseExpr();
      expect(")");
      return e;
    }
    throw new Error("Unexpected token: " + JSON.stringify(t));
  }

  const ast = parseExpr();
  if (pos < tokens.length) throw new Error("Trailing tokens");
  return ast;
}

/* ------------------------------------------------------------------ */
/* evaluator                                                           */
/* ------------------------------------------------------------------ */

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  if (typeof v === "boolean") return v ? 1 : 0;
  const n = typeof v === "number" ? v : parseFloat(v);
  if (isNaN(n)) throw new Error("#VALUE!");
  return n;
};
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

function rangeCells(fromRef, toRef, getCell, sheet) {
  const a = parseRef(fromRef);
  const b = parseRef(toRef);
  if (!a || !b) throw new Error("#REF!");
  const r1 = Math.min(a.row, b.row), r2 = Math.max(a.row, b.row);
  const c1 = Math.min(a.col, b.col), c2 = Math.max(a.col, b.col);
  const out = [];
  for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) out.push(getCell(r, c, sheet));
  return out;
}

// Flatten call args into a value list (ranges expand to their cells).
function flatten(argNodes, getCell) {
  const vals = [];
  argNodes.forEach((node) => {
    if (node.type === "range") vals.push(...rangeCells(node.from, node.to, getCell, node.sheet));
    else vals.push(evalNode(node, getCell));
  });
  return vals;
}

// Bounds of a range (or single-ref) argument node — for the functions that
// need the range SHAPE (COUNTIF/SUMIF/VLOOKUP…), not just its values.
function rangeBounds(node) {
  if (!node) throw new Error("#REF!");
  if (node.type === "range") {
    const a = parseRef(node.from);
    const b = parseRef(node.to);
    if (!a || !b) throw new Error("#REF!");
    return {
      r1: Math.min(a.row, b.row), r2: Math.max(a.row, b.row),
      c1: Math.min(a.col, b.col), c2: Math.max(a.col, b.col),
      sheet: node.sheet,
    };
  }
  if (node.type === "ref") {
    const a = parseRef(node.ref);
    if (!a) throw new Error("#REF!");
    return { r1: a.row, r2: a.row, c1: a.col, c2: a.col, sheet: node.sheet };
  }
  throw new Error("#VALUE!");
}

// Excel criteria matcher for COUNTIF/SUMIF/AVERAGEIF: supports ">5",
// ">=5", "<5", "<=5", "<>x", "=x", plain values, and * / ? wildcards.
function makeCriteria(raw) {
  const s = String(raw == null ? "" : raw);
  const m = /^(>=|<=|<>|>|<|=)?([\s\S]*)$/.exec(s);
  const op = m[1] || "=";
  const rhs = m[2];
  const rhsNum = rhs !== "" && !isNaN(parseFloat(rhs)) && isFinite(Number(rhs)) ? Number(rhs) : null;

  if (op === "=" && rhsNum == null && /[*?]/.test(rhs)) {
    // Wildcard text match (case-insensitive, whole-value).
    const rx = new RegExp(
      "^" + rhs.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*").replace(/\?/g, ".") + "$",
      "i"
    );
    return (v) => rx.test(String(v == null ? "" : v));
  }

  return (v) => {
    const vNum = typeof v === "number" ? v : (v !== "" && v != null && !isNaN(parseFloat(v)) ? parseFloat(v) : null);
    if (rhsNum != null && vNum != null) {
      switch (op) {
        case ">": return vNum > rhsNum;
        case ">=": return vNum >= rhsNum;
        case "<": return vNum < rhsNum;
        case "<=": return vNum <= rhsNum;
        case "<>": return vNum !== rhsNum;
        default: return vNum === rhsNum;
      }
    }
    const a = String(v == null ? "" : v).toLowerCase();
    const b = String(rhs).toLowerCase();
    switch (op) {
      case "<>": return a !== b;
      case ">": return a > b;
      case ">=": return a >= b;
      case "<": return a < b;
      case "<=": return a <= b;
      default: return a === b;
    }
  };
}

// Shared COUNTIF/SUMIF/AVERAGEIF core.
function iterCriteria(argNodes, getCell, withSumRange) {
  const rng = rangeBounds(argNodes[0]);
  const test = makeCriteria(evalNode(argNodes[1], getCell));
  let sumRng = rng;
  if (withSumRange && argNodes[2]) {
    const s = rangeBounds(argNodes[2]);
    // Excel aligns the sum range to the criteria range's top-left.
    sumRng = { r1: s.r1, c1: s.c1, r2: s.r1 + (rng.r2 - rng.r1), c2: s.c1 + (rng.c2 - rng.c1), sheet: s.sheet };
  }
  const hits = [];
  for (let r = rng.r1; r <= rng.r2; r++) {
    for (let c = rng.c1; c <= rng.c2; c++) {
      if (test(getCell(r, c, rng.sheet))) {
        hits.push(getCell(sumRng.r1 + (r - rng.r1), sumRng.c1 + (c - rng.c1), sumRng.sheet));
      }
    }
  }
  return hits;
}

// VLOOKUP/HLOOKUP core. vertical=true scans rows of the first column.
function lookup(argNodes, getCell, vertical) {
  const target = evalNode(argNodes[0], getCell);
  const rng = rangeBounds(argNodes[1]);
  const idx = toNum(evalNode(argNodes[2], getCell));
  const approx = argNodes[3] !== undefined ? toBool(evalNode(argNodes[3], getCell)) : true;
  const count = vertical ? (rng.r2 - rng.r1 + 1) : (rng.c2 - rng.c1 + 1);
  const keyAt = (i) => (vertical ? getCell(rng.r1 + i, rng.c1, rng.sheet) : getCell(rng.r1, rng.c1 + i, rng.sheet));
  const valAt = (i) => (vertical
    ? getCell(rng.r1 + i, rng.c1 + idx - 1, rng.sheet)
    : getCell(rng.r1 + idx - 1, rng.c1 + i, rng.sheet));
  if (idx < 1 || (vertical ? rng.c1 + idx - 1 > rng.c2 : rng.r1 + idx - 1 > rng.r2)) throw new Error("#REF!");

  const tNum = typeof target === "number" ? target : (target !== "" && !isNaN(parseFloat(target)) ? parseFloat(target) : null);
  if (!approx) {
    for (let i = 0; i < count; i++) {
      const k = keyAt(i);
      const kNum = typeof k === "number" ? k : (k !== "" && k != null && !isNaN(parseFloat(k)) ? parseFloat(k) : null);
      const equal = tNum != null && kNum != null
        ? kNum === tNum
        : String(k == null ? "" : k).toLowerCase() === String(target == null ? "" : target).toLowerCase();
      if (equal) return valAt(i);
    }
    throw new Error("#N/A");
  }
  // Approximate: largest key <= target (assumes sorted ascending, like Excel).
  let best = -1;
  for (let i = 0; i < count; i++) {
    const k = keyAt(i);
    const kNum = typeof k === "number" ? k : (k !== "" && k != null && !isNaN(parseFloat(k)) ? parseFloat(k) : null);
    if (tNum != null && kNum != null) {
      if (kNum <= tNum) best = i; else break;
    } else if (String(k).toLowerCase() <= String(target).toLowerCase()) {
      best = i;
    }
  }
  if (best === -1) throw new Error("#N/A");
  return valAt(best);
}

const FUNCS = {
  SUM: (a, g) => flatten(a, g).reduce((s, v) => s + (v === "" || v == null ? 0 : toNum(v)), 0),
  PRODUCT: (a, g) => flatten(a, g).reduce((s, v) => s * (v === "" || v == null ? 1 : toNum(v)), 1),
  AVERAGE: (a, g) => { const n = flatten(a, g).filter((v) => v !== "" && v != null); return n.reduce((s, v) => s + toNum(v), 0) / (n.length || 1); },
  AVG: (a, g) => FUNCS.AVERAGE(a, g),
  MIN: (a, g) => Math.min(...flatten(a, g).filter((v) => v !== "" && v != null).map(toNum)),
  MAX: (a, g) => Math.max(...flatten(a, g).filter((v) => v !== "" && v != null).map(toNum)),
  COUNT: (a, g) => flatten(a, g).filter((v) => v !== "" && v != null && !isNaN(parseFloat(v))).length,
  COUNTA: (a, g) => flatten(a, g).filter((v) => v !== "" && v != null).length,
  ROUND: (a, g) => { const x = toNum(evalNode(a[0], g)); const d = a[1] ? toNum(evalNode(a[1], g)) : 0; const f = Math.pow(10, d); return Math.round(x * f) / f; },
  ABS: (a, g) => Math.abs(toNum(evalNode(a[0], g))),
  SQRT: (a, g) => Math.sqrt(toNum(evalNode(a[0], g))),
  POWER: (a, g) => Math.pow(toNum(evalNode(a[0], g)), toNum(evalNode(a[1], g))),
  MOD: (a, g) => toNum(evalNode(a[0], g)) % toNum(evalNode(a[1], g)),
  INT: (a, g) => Math.floor(toNum(evalNode(a[0], g))),
  IF: (a, g) => (toBool(evalNode(a[0], g)) ? evalNode(a[1], g) : (a[2] !== undefined ? evalNode(a[2], g) : false)),
  AND: (a, g) => flatten(a, g).every(toBool),
  OR: (a, g) => flatten(a, g).some(toBool),
  NOT: (a, g) => !toBool(evalNode(a[0], g)),
  COUNTBLANK: (a, g) => flatten(a, g).filter((v) => v === "" || v == null).length,
  COUNTIF: (a, g) => iterCriteria(a, g, false).length,
  SUMIF: (a, g) => iterCriteria(a, g, true).reduce((s, v) => s + (v === "" || v == null ? 0 : toNum(v)), 0),
  AVERAGEIF: (a, g) => {
    const hits = iterCriteria(a, g, true).filter((v) => v !== "" && v != null);
    if (hits.length === 0) throw new Error("#DIV/0!");
    return hits.reduce((s, v) => s + toNum(v), 0) / hits.length;
  },
  IFERROR: (a, g) => {
    try {
      const v = evalNode(a[0], g);
      if (typeof v === "string" && v[0] === "#") return a[1] !== undefined ? evalNode(a[1], g) : "";
      return v;
    } catch (_) {
      return a[1] !== undefined ? evalNode(a[1], g) : "";
    }
  },
  VLOOKUP: (a, g) => lookup(a, g, true),
  HLOOKUP: (a, g) => lookup(a, g, false),
  ROUNDUP: (a, g) => { const x = toNum(evalNode(a[0], g)); const d = a[1] ? toNum(evalNode(a[1], g)) : 0; const f = Math.pow(10, d); return (x < 0 ? -1 : 1) * Math.ceil(Math.abs(x) * f) / f; },
  ROUNDDOWN: (a, g) => { const x = toNum(evalNode(a[0], g)); const d = a[1] ? toNum(evalNode(a[1], g)) : 0; const f = Math.pow(10, d); return (x < 0 ? -1 : 1) * Math.floor(Math.abs(x) * f) / f; },
  CEILING: (a, g) => { const x = toNum(evalNode(a[0], g)); const s = a[1] ? toNum(evalNode(a[1], g)) : 1; return s === 0 ? 0 : Math.ceil(x / s) * s; },
  FLOOR: (a, g) => { const x = toNum(evalNode(a[0], g)); const s = a[1] ? toNum(evalNode(a[1], g)) : 1; return s === 0 ? 0 : Math.floor(x / s) * s; },
  TODAY: () => new Date().toLocaleDateString(),
  NOW: () => new Date().toLocaleString(),
  CONCAT: (a, g) => flatten(a, g).map(toStr).join(""),
  CONCATENATE: (a, g) => flatten(a, g).map(toStr).join(""),
  LEN: (a, g) => toStr(evalNode(a[0], g)).length,
  LEFT: (a, g) => toStr(evalNode(a[0], g)).slice(0, a[1] ? toNum(evalNode(a[1], g)) : 1),
  RIGHT: (a, g) => { const s = toStr(evalNode(a[0], g)); const k = a[1] ? toNum(evalNode(a[1], g)) : 1; return s.slice(s.length - k); },
  MID: (a, g) => { const s = toStr(evalNode(a[0], g)); const st = toNum(evalNode(a[1], g)); const l = toNum(evalNode(a[2], g)); return s.substr(st - 1, l); },
  UPPER: (a, g) => toStr(evalNode(a[0], g)).toUpperCase(),
  LOWER: (a, g) => toStr(evalNode(a[0], g)).toLowerCase(),
  TRIM: (a, g) => toStr(evalNode(a[0], g)).trim(),
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (v === "" || v == null) return false;
  if (/^true$/i.test(v)) return true;
  if (/^false$/i.test(v)) return false;
  return !!v;
};

function evalNode(node, getCell) {
  switch (node.type) {
    case "num": return node.v;
    case "str": return node.v;
    case "bool": return node.v;
    case "neg": return -toNum(evalNode(node.arg, getCell));
    case "pct": return toNum(evalNode(node.arg, getCell)) / 100;
    case "ref": {
      const r = parseRef(node.ref);
      if (!r) throw new Error("#REF!");
      const v = getCell(r.row, r.col, node.sheet);
      return v == null ? "" : v;
    }
    case "range": {
      // A bare range used as a scalar -> first cell (Excel-ish).
      const cells = rangeCells(node.from, node.to, getCell, node.sheet);
      return cells.length ? cells[0] : "";
    }
    case "call": {
      const fn = FUNCS[node.name];
      if (!fn) throw new Error("#NAME?");
      return fn(node.args, getCell);
    }
    case "bin": {
      const { op } = node;
      if (op === "&") return toStr(evalNode(node.left, getCell)) + toStr(evalNode(node.right, getCell));
      if (["=", "<>", "<", "<=", ">", ">="].includes(op)) {
        const l = evalNode(node.left, getCell);
        const r = evalNode(node.right, getCell);
        const ln = typeof l === "number" ? l : (isNaN(parseFloat(l)) ? l : parseFloat(l));
        const rn = typeof r === "number" ? r : (isNaN(parseFloat(r)) ? r : parseFloat(r));
        switch (op) {
          case "=": return ln === rn;
          case "<>": return ln !== rn;
          case "<": return ln < rn;
          case "<=": return ln <= rn;
          case ">": return ln > rn;
          case ">=": return ln >= rn;
          default: return false;
        }
      }
      const a = toNum(evalNode(node.left, getCell));
      const b = toNum(evalNode(node.right, getCell));
      switch (op) {
        case "+": return a + b;
        case "-": return a - b;
        case "*": return a * b;
        case "/": if (b === 0) throw new Error("#DIV/0!"); return a / b;
        case "^": return Math.pow(a, b);
        default: throw new Error("bad op");
      }
    }
    default: throw new Error("bad node");
  }
}

/**
 * Evaluate a formula string (with or without leading "=").
 * @param {string} formula
 * @param {(row:number,col:number)=>any} getCell  0-based resolver
 * @returns {number|string|boolean} computed value, or an Excel-like error string
 */
// Parsed-AST cache: the same formulas are re-evaluated on every edit, so
// tokenizing/parsing once per distinct formula string keeps recalculation
// cheap on formula-heavy workbooks.
const AST_CACHE = new Map();
const AST_CACHE_MAX = 5000;
function parseCached(src) {
  let ast = AST_CACHE.get(src);
  if (ast === undefined) {
    ast = parse(tokenize(src));
    if (AST_CACHE.size >= AST_CACHE_MAX) AST_CACHE.clear();
    AST_CACHE.set(src, ast);
  }
  return ast;
}

/**
 * Every cell/range a formula reads, as
 *   [{ sheet: string|undefined, r1, c1, r2, c2 }]   (0-based, inclusive)
 * `sheet` is undefined for references to the formula's own sheet.
 * Returns null when the formula cannot be parsed (caller should treat it as
 * "depends on anything").
 */
export function getFormulaRefs(formula) {
  try {
    let src = String(formula || "");
    if (src[0] === "=") src = src.slice(1);
    if (src.trim() === "") return [];
    const ast = parseCached(src);
    const out = [];
    const walk = (node) => {
      if (!node) return;
      if (node.type === "ref") {
        const a = parseRef(node.ref);
        if (a) out.push({ sheet: node.sheet, r1: a.row, c1: a.col, r2: a.row, c2: a.col });
      } else if (node.type === "range") {
        const a = parseRef(node.from);
        const b = parseRef(node.to);
        if (a && b) {
          out.push({
            sheet: node.sheet,
            r1: Math.min(a.row, b.row), r2: Math.max(a.row, b.row),
            c1: Math.min(a.col, b.col), c2: Math.max(a.col, b.col),
          });
        }
      } else if (node.type === "call") {
        node.args.forEach(walk);
      } else if (node.type === "bin") {
        walk(node.left); walk(node.right);
      } else if (node.type === "neg" || node.type === "pct") {
        walk(node.arg);
      }
    };
    walk(ast);
    return out;
  } catch (e) {
    return null;
  }
}

export function evaluateFormula(formula, getCell) {
  try {
    let src = String(formula || "");
    if (src[0] === "=") src = src.slice(1);
    if (src.trim() === "") return "";
    const ast = parseCached(src);
    const result = evalNode(ast, getCell);
    if (typeof result === "boolean") return result ? "TRUE" : "FALSE";
    if (typeof result === "number") {
      if (!isFinite(result)) return "#NUM!";
      // trim floating noise
      return Math.round(result * 1e10) / 1e10;
    }
    return result;
  } catch (e) {
    const msg = String(e && e.message);
    return msg[0] === "#" ? msg : "#ERROR!";
  }
}

export function isFormula(text) {
  return typeof text === "string" && text.trim()[0] === "=";
}

export default evaluateFormula;
